import { Checkbox, FormField } from '@vwfs-bronson/bronson-react';
import { useField } from 'formik';
import React, { FocusEventHandler, MouseEventHandler } from 'react';
import { getInputErrorMessageComponentAndId } from '../errors';
import { InfoIcon } from '../../info-icon';

export type ValidatedCheckboxProps = {
    name: string;
    label: string | React.ReactNode;
    ariaLabel?: string;
    testId?: string;
    isMandatory?: boolean;
    onFocus?: FocusEventHandler<HTMLInputElement>;
    disabled?: boolean;
    id?: string;
    hint?: string;
    withHtmlValidationWarning?: boolean;
    className?: string;
    onLinkClick?: MouseEventHandler;
    tooltip?: string | React.ReactNode;
    inlineTooltip?: string | React.ReactNode;
    formLabel?: string;
    isCheckboxMandatory?: boolean;
    children?: React.ReactNode;
};

export const ValidatedCheckbox: React.FC<ValidatedCheckboxProps> = ({
    name,
    label,
    ariaLabel,
    testId,
    isMandatory = false,
    onFocus,
    disabled,
    id,
    hint,
    withHtmlValidationWarning,
    className,
    tooltip,
    inlineTooltip,
    formLabel,
    isCheckboxMandatory,
    onLinkClick,
}) => {
    const [field, meta] = useField({ name, type: 'checkbox' });

    const notion = isCheckboxMandatory ? <span className="c-form-field__notion">* </span> : null;
    const infoIcon = tooltip ? <InfoIcon text={tooltip} /> : null;
    const inlineInfoIcon = inlineTooltip ? <InfoIcon text={inlineTooltip} /> : null;
    const { errorMessageComponent, errorMessageId } = getInputErrorMessageComponentAndId({
        name,
        id,
        meta,
        testId,
        withHtmlValidationWarning,
    });

    return (
        <FormField
            type="checkbox"
            testId={`${testId}FormField`}
            required={isMandatory}
            hint={hint}
            onClick={onLinkClick}
            errorMessage={errorMessageComponent}
            labelText={formLabel}
            labelForId={id}
            infoIcon={infoIcon}
        >
            <Checkbox
                id={id}
                onFocus={onFocus}
                {...field}
                testId={testId}
                disabled={disabled}
                required={isMandatory}
                className={className}
                aria-describedby={errorMessageId}
                aria-label={ariaLabel || (typeof label === 'string' && label) || ''}
            >
                {label}
                {notion}
                {inlineInfoIcon}
            </Checkbox>
        </FormField>
    );
};
